// @flow
import $ from 'jquery';
import ReactDOM from 'react-dom';

import type { LocalizedNewsArticle } from '../../utils/types';
import type { ScriptCommonArgs } from '../types';

import { News } from '../../components/Blocks/News/News';
import { getMainFontClass, getTitleFontClass } from '../../utils/common-helper';
import {
  formatNewsDate,
  getNewsHash,
  DEFAULT_IMAGE,
  HORIZONTAL_NEWS_LAYOUT,
  MOBILE_SCREEN_WIDTH,
  STANDARD_NEWS_AMOUNT,
  SMALL_NEWS_AMOUNT
} from '../../utils/news-helper';
import { getNews } from '../helpers/common';
import * as Button from '../ui-components/editor/button';

let newsContainer = [];
let newsCount = 0;

const isShowLoadMoreButton = () => newsContainer.length < newsCount;

const changeWindowHash = (hash: string) => {
  /* eslint-disable no-restricted-globals */
  if (history.pushState) {
    if (!hash) {
      history.pushState(null, '', window.location.href.split('#')[0]); return;
    }
    history.pushState(null, '', hash);
  } else {
    window.location.hash = hash;
  }
  /* eslint-enable no-restricted-globals */
};

const prepareLongText = longText => {
  const $container = $(document.createElement('div'));
  $container.append($(longText));
  $container.find('[style]')
    .each((index, element) => {
      $(element)
        .removeAttr('style');
    });
  $container.find('img')
    .each((index, element) => {
      $(element)
        .removeAttr('width');
      $(element)
        .removeAttr('height');
      $(element)
        .addClass('modal-image');
      $('<div class="modal-image"></div>')
        // $FlowFixMe
        .attr('style', `background-image: url("${element.src}");`)
        .insertAfter($(element));
      $(element).remove();
    });

  return $container.html();
};

const handleClickToScrollToPacks = () => {
  const packs = document.querySelectorAll('.packs');
  if (!packs || !packs.length) {
    return;
  }
  const [element] = packs;
  element.scrollIntoView({ behavior: 'smooth' });
};

export default async ({
  blockId, data: {
    launcherNewsInfo,
    projectId,
    locale,
    environment,
    isPreviewFrameMode,
    pageTheme,
    landingTheme,
    originalTextColor,
    headerFont,
    mainFont,
    subscriptionPurchase
  }
}: ScriptCommonArgs): Promise<void> => {
  const blockOptions = launcherNewsInfo.find(data => data.blockId === blockId);
  if (!blockOptions) {
    return;
  }

  const DEFAULT_BG_IMAGE = $('.editor-background-image').css('background-image');

  const fetchNews = async () => {
    const needLoadLessNews = window.innerWidth < MOBILE_SCREEN_WIDTH
      || blockOptions.template === HORIZONTAL_NEWS_LAYOUT;
    const limit = needLoadLessNews ? SMALL_NEWS_AMOUNT : STANDARD_NEWS_AMOUNT;
    const data = await getNews({
      launcherId: blockOptions.launcherId,
      projectId,
      locale,
      offset: newsContainer.length,
      limit,
      environment,
      isPreviewFrameMode
    });
    newsCount = data.count;
    newsContainer = [...newsContainer, ...data.news];
    return newsContainer;
  };

  const createNewsModal = (news: LocalizedNewsArticle) => {
    const back = document.createElement('div');
    const date = formatNewsDate(news.date);

    const exitFunc = (event: Event) => {
      if (event.target instanceof Element) {
        const { classList } = event.target;
        if (classList.contains('exit-action')) {
          $(back).remove();
          if (document.body) {
            document.body.style.overflow = '';
          }
          changeWindowHash('');
        }
      }
    };

    back.addEventListener('click', exitFunc);
    back.classList.add('modal-backdrop');
    back.classList.add('exit-action');
    const titleToHash = getNewsHash(news.title);
    changeWindowHash(titleToHash);
    const modalContent = document.createElement('div');
    modalContent.classList.add('modal-content');
    modalContent.classList.add(getMainFontClass(mainFont));
    modalContent.classList.add(getTitleFontClass(headerFont));
    $(modalContent)
      .html(
        '<div class="modal-title-image">'
        + '    <div class="modal-exit-btn exit-action">'
        + '<svg class="exit-action" width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">'
        + `<line class="exit-action" x1="0.783279" y1="1.06194" x2="16.9651" y2="17.2438" stroke-width="2" stroke="${originalTextColor}"/>`
        + `<line class="exit-action" x1="0.716721" y1="17.2436" x2="16.8986" y2="1.06171" stroke-width="2" stroke="${originalTextColor}"/>`
        + '</svg>'
        + '</div>'
        + '</div>\n'
        + '<div class="modal-text-wrapper">'
        + '    <div class="modal-info--wrapper">'
        + `        <span class="modal-info">${(date).toLocaleDateString(locale)}</span>\n`
        + '    </div>\n'
        + `    <h2 class="modal-title">${news.title}</h2>\n`
        + `<div class="modal-text-container">${prepareLongText(news.text_long)}</div>`
        + '</div>'
      );
    $(modalContent)
      .find('.modal-title-image')
      .attr('style', `background-image:${news.image === '' || news.image === DEFAULT_IMAGE ? DEFAULT_BG_IMAGE : `url("${news.image}")`};`);
    $(back).append(modalContent);
    if (document.body) {
      document.body.style.overflow = 'hidden';
    }
    $('.preview-page').append(back);
    $(back)
      .find('.modal-exit-btn')
      .on('click', exitFunc);

    $(modalContent)
      .find('.modal-text-wrapper')
      .scroll(({ target: element }) => {
        const offset = $(element).scrollTop();
        const $image = $(modalContent).find('.modal-title-image');
        const $text = $(modalContent).find('.modal-text-container');
        const freeSpace = $(modalContent).outerHeight() - $text.outerHeight();
        const canMinimize = freeSpace < 300;

        if (offset > 0 && canMinimize) {
          $image.addClass('modal-title-image--small');
        } else {
          $image.removeClass('modal-title-image--small');
        }
      });
    $(modalContent).find('iframe, video').each((index, element) => {
      $(element).wrap('<div class="modal-video-container"></div>');
    });
  };

  const renderNewsContent = news => {
    const NewsContent = News.renderNewsTemplate({
      news,
      handleClick: createNewsModal,
      handleScrollToPacks: handleClickToScrollToPacks,
      template: blockOptions.template,
      launcherId: blockOptions.launcherId,
      locale,
      pageTheme,
      landingTheme,
      subscriptionPlans: subscriptionPurchase
    });
    const mountNode = document.querySelector(`#news-${blockId} [data-news-content]`);
    if (!NewsContent || !mountNode) {
      return;
    }
    ReactDOM.unmountComponentAtNode(mountNode);
    ReactDOM.render(NewsContent, mountNode);
  };

  const news = await fetchNews();
  renderNewsContent(news);

  if (isShowLoadMoreButton()) {
    const button = document.querySelector(`#news-${blockId} [data-news-load-more-button]`);
    if (!button) {
      return;
    }

    Button.showButton(button);
    button.addEventListener('click', async () => {
      Button.setFetchingState(button);

      const news = await fetchNews();
      renderNewsContent(news);

      Button.unsetFetchingState(button);
      if (!isShowLoadMoreButton()) {
        Button.hideButton(button);
      }
    });
  }
};
